/**
 * Dashboard Statistics Grid Component (debug-enhanced)
 *
 * File-level:
 * - Renders a responsive grid of stat cards.
 * - Adds extensive debug logging including a document capture-phase click listener
 *   so we can observe which DOM element actually receives the pointer event and
 *   whether the event is being intercepted before reaching the card handlers.
 *
 * Notes:
 * - Keep this debug helper temporary. Remove capture listener when issue is resolved.
 */

import React, { KeyboardEvent, useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

interface StatItem {
  /** Label shown under the stat */
  label: string;
  /** Value displayed prominently */
  value: string;
  /** Icon component (lucide-react) */
  icon: any;
  /** Color gradient for icon/bg */
  color: string;
  /** Card background gradient */
  bgColor: string;
  /** Small trend label */
  trend: string;
}

interface StatsGridProps {
  /** Array of stat items to render */
  stats: StatItem[];
  /**
   * Optional click handler called with (label, index) when a stat card is activated.
   * Useful to show detail modals (e.g. Profile Views details).
   */
  onStatClick?: (label: string, index: number) => void;
}

/**
 * StatsGrid component
 *
 * - Responsible for rendering a responsive grid of stat cards.
 * - Each card is keyboard accessible (Enter/Space) and clickable when onStatClick is provided.
 * - Adds debug logging for click/key events so parent handlers can be traced.
 */
export default function StatsGrid({ stats, onStatClick }: StatsGridProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  /**
   * handleKeyDown
   *
   * @param e KeyboardEvent - keyboard event from card
   * @param label string - stat label
   * @param index number - stat index
   * @description Activates click handler when Enter or Space is pressed
   */
  function handleKeyDown(e: KeyboardEvent, label: string, index: number) {
    if (!onStatClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Debug log - key activation
      // eslint-disable-next-line no-console
      console.debug('[StatsGrid] key activate:', { key: e.key, label, index });
      onStatClick(label, index);
    }
  }

  /**
   * handleClick
   *
   * @param label string
   * @param index number
   * @description Centralized onClick handler: logs and forwards to parent.
   */
  function handleClick(label: string, index: number) {
    // Debug log - mouse click activation
    // eslint-disable-next-line no-console
    console.debug('[StatsGrid] clicked:', { label, index });
    if (onStatClick) onStatClick(label, index);
  }

  useEffect(() => {
    /**
     * Document capture-phase click listener
     * - Runs in capture phase (useCapture = true) to catch the earliest DOM event.
     * - Logs event target, path length, and whether defaultPrevented is true.
     */
    function onDocClickCapture(ev: MouseEvent) {
      try {
        const target = ev.target as Element | null;
        const tag = target ? target.tagName : 'null';
        const cls = target ? (target.getAttribute('class') || '') : '';
        const path = (ev as any).composedPath ? (ev as any).composedPath() : (ev as any).path || [];
        // eslint-disable-next-line no-console
        console.debug('[StatsGrid][doc-capture] click', {
          tag,
          class: cls,
          pathLength: Array.isArray(path) ? path.length : undefined,
          defaultPrevented: ev.defaultPrevented,
          time: Date.now(),
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[StatsGrid][doc-capture] error', err);
      }
    }

    // Use capture phase so this runs before usual bubble handlers and can reveal intercepting overlays
    document.addEventListener('click', onDocClickCapture, true);

    return () => {
      document.removeEventListener('click', onDocClickCapture, true);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        const clickable = typeof onStatClick === 'function';

        return (
          <div
            key={index}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => handleClick(stat.label, index) : undefined}
            onMouseDown={clickable ? (e) => {
              // MouseDown fires before Click; useful for tracing click capture issues
              // eslint-disable-next-line no-console
              console.debug('[StatsGrid] mousedown:', { label: stat.label, index, button: (e as any).button });
            } : undefined}
            onKeyDown={clickable ? (e) => handleKeyDown(e as KeyboardEvent, stat.label, index) : undefined}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex((i) => (i === index ? null : i))}
            className={`${clickable ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded' : ''}`}
            aria-pressed="false"
            data-testid={`stat-card-${index}`}
          >
            <Card className={`${stat.bgColor} border-0 shadow-lg hover:shadow-xl transition-all duration-300 ${clickable ? 'hover:scale-105' : ''} ${hoverIndex === index ? 'ring-2 ring-pink-500' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
                    <div className="flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
                      <span className="text-xs text-green-600 font-medium">{stat.trend}</span>
                    </div>
                  </div>
                  <div
                    className={`p-3 rounded-xl bg-gradient-to-r ${stat.color}`}
                    // extra debug: log clicks directly on the icon container
                    onClick={(e) => {
                      // Stop here so we can observe whether clicks reach this element
                      // eslint-disable-next-line no-console
                      console.debug('[StatsGrid] icon container clicked:', { label: stat.label, index });
                    }}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
}